import { ZERO } from '@/constants/common';
import { PerfectMultiSearchParams } from '@/types/search';
import { ThunkConfig } from '@/types/store';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getError } from '@/utils/error-report/getError';
import { selectTab } from '@/redux/drawer/drawer.slice';
import {
  resetHighlightReactionsData,
  setHighlightReactionsByIds,
  setLoadingHighlightReactionsData,
} from '@/redux/reactions/reactions.slice';
import {
  clearSearchModelElements,
  setLoadingSearchModelElements,
  setModelElementSearchByIds,
} from '@/redux/modelElements/modelElements.slice';
import { BioEntitySearchedIdObject } from '@/redux/bioEntity/bioEntity.types';
import { addNumbersToEntityNumberData } from '../../entityNumber/entityNumber.slice';
import { MULTI_BIO_ENTITY_FETCHING_ERROR_PREFIX } from '../bioEntity.constants';
import { getBioEntity } from './getBioEntity';

type GetMultiBioEntityProps = PerfectMultiSearchParams;

export const getMultiBioEntity = createAsyncThunk<
  {
    searchedReactions: BioEntitySearchedIdObject[];
    searchedModelElements: BioEntitySearchedIdObject[];
  },
  GetMultiBioEntityProps,
  ThunkConfig
>(
  'project/getMultiBioEntity',
  // eslint-disable-next-line consistent-return
  async ({ searchQueries, isPerfectMatch }, { dispatch }) => {
    try {
      dispatch(clearSearchModelElements());
      dispatch(setLoadingHighlightReactionsData());
      dispatch(resetHighlightReactionsData());
      dispatch(setLoadingSearchModelElements());
      const asyncGetBioEntityFunctions = searchQueries.map(searchQuery =>
        dispatch(
          getBioEntity({ searchQuery, isPerfectMatch, addNumbersToEntityNumber: false }),
        ).unwrap(),
      );

      const bioEntityContentsActions = await Promise.all(asyncGetBioEntityFunctions);
      const modelElementsIds: number[] = [];
      const searchedReactions: Record<string, BioEntitySearchedIdObject> = {};
      const searchedModelElements: Record<string, BioEntitySearchedIdObject> = {};
      bioEntityContentsActions.forEach(bioEntityContentsAction => {
        if (!bioEntityContentsAction) {
          return;
        }
        bioEntityContentsAction.searchedReactions.forEach(searchedReaction => {
          const key = `${searchedReaction.id}-${searchedReaction.modelId}`;
          if (!searchedReactions[key]) {
            searchedReactions[key] = searchedReaction;
          }
        });
        bioEntityContentsAction.searchedModelElements.forEach(searchedModelElement => {
          const key = `${searchedModelElement.id}-${searchedModelElement.modelId}`;
          if (!searchedModelElements[key]) {
            searchedModelElements[key] = searchedModelElement;
          }
        });
        modelElementsIds.push(...bioEntityContentsAction.modelElementsIds);
      });
      dispatch(addNumbersToEntityNumberData(modelElementsIds));

      const searchedReactionsArray: BioEntitySearchedIdObject[] = Object.values(searchedReactions);
      const searchedModelElementsArray: BioEntitySearchedIdObject[] =
        Object.values(searchedModelElements);
      dispatch(
        setHighlightReactionsByIds({
          searchedReactions: searchedReactionsArray,
        }),
      );
      dispatch(
        setModelElementSearchByIds({
          searchedModelElements: searchedModelElementsArray,
        }),
      );
      dispatch(selectTab(searchQueries[ZERO]));
      return {
        searchedReactions: searchedReactionsArray,
        searchedModelElements: searchedModelElementsArray,
      };
    } catch (error) {
      return Promise.reject(getError({ error, prefix: MULTI_BIO_ENTITY_FETCHING_ERROR_PREFIX }));
    }
  },
);
